import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm, useFormContext } from 'react-hook-form';
import {
  createSnowflakeConnection,
  CreateSnowflakeConnectionPayload,
  fetchSnowflakeConnections,
  fetchSnowflakeTables,
} from '../../../../services/queries/SnowflakeSyncQueries';

/** Sentinel value used in the connection Select to open the create-connection dialog. */
const CREATE_NEW_SENTINEL = '__create_new__';

// ─── Create Connection Dialog ─────────────────────────────────────────────────

interface CreateConnectionDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (id: number) => void;
}

interface CreateConnectionFormValues {
  name: string;
  database: string;
  schema: string;
  role: string;
}

function CreateConnectionDialog({ open, onClose, onCreated }: CreateConnectionDialogProps) {
  const queryClient = useQueryClient();
  const [createError, setCreateError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateConnectionFormValues>({
    defaultValues: { name: '', database: '', schema: '', role: '' },
  });

  const { mutate: create, isPending } = useMutation({
    mutationFn: (payload: CreateSnowflakeConnectionPayload) => createSnowflakeConnection(payload),
    onSuccess: (connection) => {
      queryClient.invalidateQueries({ queryKey: ['snowflake-connections'] });
      reset();
      setCreateError(null);
      onCreated(connection.id);
    },
    onError: () => {
      setCreateError('Failed to create connection. Check the name is unique and try again.');
    },
  });

  const onSubmit = (values: CreateConnectionFormValues) => {
    create({
      name: values.name,
      mode: 'sharedForesight',
      database: values.database || undefined,
      schema: values.schema || undefined,
      role: values.role || undefined,
    });
  };

  const handleClose = () => {
    reset();
    setCreateError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>New Snowflake Connection</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            {createError && <Alert severity='error'>{createError}</Alert>}
            <Typography variant='body2' color='text.secondary'>
              Creates a <strong>sharedForesight</strong> connection — reuses Foresight&apos;s own Snowflake
              account and points at a database attached as a data share from the client. No credentials are
              stored.
            </Typography>

            <TextField
              {...register('name', { required: 'Name is required' })}
              label='Connection Name'
              placeholder='e.g. Client Snowflake Share'
              fullWidth
              required
              error={!!errors.name}
              helperText={errors.name?.message || 'A human-readable label for this connection.'}
            />

            <TextField
              {...register('database')}
              label='Database'
              placeholder='e.g. MY_CLIENT_DB'
              fullWidth
              helperText='The Snowflake database (or share database) to query. Leave blank to use the default.'
            />

            <TextField
              {...register('schema')}
              label='Schema'
              placeholder='e.g. PUBLIC'
              fullWidth
              helperText='The schema within the database. Leave blank to use the default.'
            />

            <TextField
              {...register('role')}
              label='Role (optional)'
              placeholder='e.g. FORESIGHT_READ_ROLE'
              fullWidth
              helperText='Snowflake role to use when querying. Leave blank to inherit the account default.'
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} color='secondary' variant='outlined' disabled={isPending}>
            Cancel
          </Button>
          <Button
            type='submit'
            color='secondary'
            variant='contained'
            disabled={isPending}
            startIcon={isPending ? <CircularProgress size={16} /> : undefined}
          >
            {isPending ? 'Creating…' : 'Create Connection'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// ─── Main Section ─────────────────────────────────────────────────────────────

/**
 * Field Automation config section for `sourceType === 'snowflake'`.
 *
 * Surfaces three required dropdowns (connection / table / match-key column) plus an
 * optional reporting-period column pair. Each dropdown is populated by hitting the
 * snowflake-sync API so admins never have to type a table or column name from memory.
 *
 * Scope per FD-2864: only `sharedForesight` connections are exposed. External-credential
 * connections registered on the backend are filtered out client-side. The match-key
 * resolver maps source rows to Foresight entities: `companyId` for direct numeric
 * Foresight ids, `companyProvider` (with a provider name) for the standard external-id
 * lookup pattern via the `companyProvider` table.
 */
export function SnowflakeConfigSection() {
  const { control, watch, setValue } = useFormContext();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const connectionId = watch('config.snowflakeConfig.connectionId') as number | undefined;

  const connectionsQuery = useQuery({
    queryKey: ['snowflake-connections'],
    queryFn: fetchSnowflakeConnections,
  });

  const tablesQuery = useQuery({
    queryKey: ['snowflake-tables', connectionId],
    queryFn: () => fetchSnowflakeTables(connectionId!),
    enabled: connectionId !== undefined && connectionId !== null,
  });

  const sharedConnections = (connectionsQuery.data ?? []).filter((c) => c.mode === 'sharedForesight');

  // The entity identifier column lives in entityMapping.identifierField (shared with file
  // ingestion) — clear it when the connection or table changes to avoid stale picks.
  const resetColumnDependentFields = () => {
    setValue('config.entityMapping.identifierField', undefined, { shouldDirty: true });
    setValue('config.snowflakeConfig.reportingPeriodColumn', undefined, { shouldDirty: true });
    setValue('config.snowflakeConfig.reportingPeriodSecondaryColumn', undefined, { shouldDirty: true });
    setValue('config.snowflakeConfig.sourceTimestampColumn', undefined, { shouldDirty: true });
  };

  const handleConnectionCreated = (newId: number) => {
    setCreateDialogOpen(false);
    // Auto-select the new connection and cascade-reset dependent fields.
    setValue('config.snowflakeConfig.connectionId', newId, { shouldDirty: true });
    setValue('config.snowflakeConfig.sourceTable', undefined, { shouldDirty: true });
    resetColumnDependentFields();
  };

  return (
    <Box>
      <Stack spacing={4}>
        <Typography variant='h6'>Snowflake Source</Typography>

        <Controller
          name='config.snowflakeConfig.connectionId'
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === CREATE_NEW_SENTINEL) {
                  // Don't update the field value — just open the dialog.
                  setCreateDialogOpen(true);
                  return;
                }
                const next = raw === '' ? undefined : Number(raw);
                field.onChange(next);
                // Cascade-reset dependent selections when the connection changes.
                setValue('config.snowflakeConfig.sourceTable', undefined, { shouldDirty: true });
                resetColumnDependentFields();
              }}
              select
              label='Connection'
              fullWidth
              required
              disabled={connectionsQuery.isLoading}
              helperText={connectionsQuery.isError ? 'Failed to load connections' : ''}
            >
              {sharedConnections.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name} ({c.database ?? '—'}.{c.schema ?? '—'})
                </MenuItem>
              ))}
              {sharedConnections.length > 0 && <Divider />}
              <MenuItem value={CREATE_NEW_SENTINEL} sx={{ color: 'primary.main' }}>
                <AddIcon fontSize='small' sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                Create new connection
              </MenuItem>
            </TextField>
          )}
        />

        <Controller
          name='config.snowflakeConfig.sourceTable'
          control={control}
          render={({ field }) => (
            <Autocomplete
              options={(tablesQuery.data ?? []).map((t) => t.name)}
              value={field.value ?? null}
              onChange={(_, next) => {
                field.onChange(next ?? null);
                resetColumnDependentFields();
              }}
              getOptionLabel={(opt) => {
                const match = tablesQuery.data?.find((t) => t.name === opt);
                return match ? `${match.name} (${match.kind})` : opt;
              }}
              disabled={!connectionId || tablesQuery.isLoading}
              loading={tablesQuery.isLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Source Table or View'
                  required
                  helperText={
                    tablesQuery.isError
                      ? 'Failed to load tables for this connection'
                      : 'Tables and views in the connection’s schema.'
                  }
                />
              )}
            />
          )}
        />

        <Typography variant='body2' color='text.secondary'>
          The entity identifier, reporting period, and last-modified columns are configured on the{' '}
          <strong>Ingestion Settings</strong> tab once a table is selected.
        </Typography>
      </Stack>

      <CreateConnectionDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={handleConnectionCreated}
      />
    </Box>
  );
}
